import * as React from 'react'
// libs
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
// hooks
import { useDashboardsAPI } from 'hooks/useDashboardsAPI'
import { useChartNotifications } from 'hooks/useChartNotifications'
// components
import AddItemButton from './components/AddItemButton'
import DashboardWidgets from './components/DashboardWidgets'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
import MainLayoutContainer from 'containers/MainLayout/index'
import RefreshChartsButton from 'components/CTAs/RefreshChartsButton'
import DashboardSettingsDrawer from '../../Modals/DashboardSettingsModal'
import DashboardWidgetSettingsDrawer from 'components/Modals/WidgetSettingsModal'
// constants
import { Modals } from 'constants/modals'
// view components
import { AddDashboardButton } from 'components/CTAs'
// views
import { Container } from '@mui/material'
// store
import {
  setDashboardsData,
  updateCurrentDashboard,
  isDashboardEditSelector,
  currentDashboardSelector
} from 'store/reducers/dashboards'
// utils
import { orderBy } from 'lodash'

const Home: React.FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()

  // Two loads may overlap: the first one to end must not show the page while the other is still running
  const [isDashboardsLoading, setIsDashboardsLoading] = React.useState(false)
  const [isItemsLoading, setIsItemsLoading] = React.useState(false)

  const isEdit = useSelector(isDashboardEditSelector)
  const currentDashboard = useSelector(currentDashboardSelector)

  // The dashboards list resolves after the user may have picked a dashboard
  const currentDashboardRef = React.useRef(currentDashboard)
  currentDashboardRef.current = currentDashboard

  const { closeSnackbar } = useSnackbar()

  const { getDashboards, getDashboardItems } = useDashboardsAPI()

  // The dashboard's charts get their new numbers when a recompute ends
  useChartNotifications()

  const getDashboardsData = async () => {
    setIsDashboardsLoading(true)

    const dashboards = await getDashboards()

    if (dashboards?.length) {
      const isCurrentAvailable = dashboards.some((dashboard) => dashboard.id === currentDashboardRef.current?.id)

      dispatch(
        setDashboardsData({
          data: orderBy(dashboards, 'createdAt'),
          // The chosen dashboard stays, the first one is only a fallback
          ...(!isCurrentAvailable && { current: dashboards[0] })
        })
      )
    }

    setIsDashboardsLoading(false)
  }

  const setDashboardItems = async () => {
    const dashboardId = currentDashboard.id

    setIsItemsLoading(true)

    const widgets = await getDashboardItems(dashboardId)

    dispatch(updateCurrentDashboard({ dashboardId, widgets: orderBy(widgets, 'item.createdAt') }))

    // A dashboard picked meanwhile is still loading: its own load hides the loader
    if (dashboardId === currentDashboardRef.current?.id) {
      setIsItemsLoading(false)
    }
  }

  React.useEffect(() => {
    getDashboardsData()
  }, [])

  React.useEffect(() => {
    if (currentDashboard) {
      setDashboardItems()
    }
  }, [currentDashboard?.id])

  React.useEffect(() => {
    return () => {
      dispatch(setDashboardsData({ edit: false }))
      closeSnackbar('dashboardEdit')
    }
  }, [location.pathname])

  return (
    <>
      <Helmet>
        <title>Home</title>
      </Helmet>
      <MainLayoutContainer
        isLoading={isDashboardsLoading || isItemsLoading}
        CTA={
          <>
            <RefreshChartsButton />
            <AddDashboardButton />
          </>
        }>
        <Container maxWidth={false}>
          <DashboardWidgets />
          {isEdit && <AddItemButton />}
        </Container>

        <DashboardSettingsDrawer />
        <DashboardWidgetSettingsDrawer />
        <ConfirmDeleteModal header='Delete Dashboard' modalType={Modals.CONFIRM_DELETE_DASHBOARD_MODAL}>
          Are you sure you want to delete this dashboard?
        </ConfirmDeleteModal>
        <ConfirmDeleteModal header={'Delete Widget'} modalType={Modals.CONFIRM_DELETE_WIDGET_MODAL}>
          Are you sure you want to delete this widget?
        </ConfirmDeleteModal>
      </MainLayoutContainer>
    </>
  )
}

export default Home
